import {
  ADDON_DWH_RENT_FREE_ROWS,
  ADDON_DWH_RENT_PRICE_PER_ROW,
} from "@stores/addons/constants";
import { formatPrice, formatQuantity } from "@common/utils/string";
import { offerDWHRent } from "@stores/config/utils";
import { METER_DWH_ROWS_KEY } from "@stores/subscriptions/constants";
import { ManageCard } from "./ManageCard";
import { hasStorage, meteredLineItem } from "../utils";
import {
  IAddOnContext,
  IUpsertContext,
  IMeteredAddOnDescriptor,
} from "../types";

const getIncludedUnits = ({ subscribed }: IAddOnContext): number =>
  subscribed?.included_units ?? ADDON_DWH_RENT_FREE_ROWS;

const getPricePerUnit = ({ subscribed, addOn }: IAddOnContext): number =>
  subscribed?.price_per_unit ??
  addOn?.default_price_per_unit ??
  ADDON_DWH_RENT_PRICE_PER_ROW;

const dwhRent = {
  productType: "dwh-rent",
  kind: "metered",
  title: "Storage",
  isVisible: (ctx: IAddOnContext) =>
    ctx.isCloudHosted &&
    (!!ctx.subscribed || (!hasStorage(ctx) && offerDWHRent(ctx.config))),

  // gated on the meter, which harbormaster only emits while this row is live;
  // prepaid `dwh` is not metered, so the key belongs to rent alone
  getUsage: (ctx: IAddOnContext) => {
    const meter = ctx.purchase.meters?.[METER_DWH_ROWS_KEY];

    if (!meter) {
      return undefined;
    }

    const usage = meter.meter_value ?? 0;
    const includedUnits = getIncludedUnits(ctx);

    return {
      hasQuota: typeof meter.meter_value === "number",
      usage,
      includedUnits,
      includedUnitsUsed: Math.min(usage, includedUnits),
      billableUnits: Math.max(0, usage - includedUnits),
    };
  },

  getIncludedUnits,

  getPricePerUnit,

  getUpsertAddOns: (ctx: IUpsertContext) => [
    { product_type: "dwh-rent", prepaid_units: 0 },
    ...ctx.getUpsertAddOnsFor("etl-connections"),
  ],

  getEstimateRowItem: (ctx) => ({
    detail: "Usage-based billing",
    price:
      ctx.addOn &&
      `${formatPrice(1_000_000 * getPricePerUnit(ctx))}/1M rows (after first ${formatQuantity(getIncludedUnits(ctx))} rows)`,
  }),

  getBillingRowItem: () => ({ detail: "Usage-based billing" }),
  getPurchaseLineItem: (ctx) => meteredLineItem(ctx, "stored rows"),

  ManageCard,
} satisfies IMeteredAddOnDescriptor;

export default dwhRent;
